import { ServiceStoreConfig } from "../types/ServiceStoreTypes";

/**
 * Batching Module Service Stores
 * Services for batch plants, tickets, materials, and mix designs
 */
export const batchingStores: ServiceStoreConfig[] = [
  {
    name: "batch-plants",
    options: {
      enableStorage: true,
      enableErrorNotifications: true,
      idField: "id",
      serviceView: {
        enableServiceView: false,
        summaryCards: (rootStore) => {
          const store = rootStore.batchPlantsStore as {
            entities?: unknown[];
          };
          const entities = store?.entities || [];
          const totalPlants = entities.length;
          const activePlants = entities.filter(
            (plant: Record<string, unknown>) =>
              plant.status === "active" || plant.isActive === true
          ).length;
          const maintenancePlants = entities.filter(
            (plant: Record<string, unknown>) =>
              plant.status === "maintenance"
          ).length;
          const offlinePlants = entities.filter(
            (plant: Record<string, unknown>) =>
              plant.status === "offline" || plant.isActive === false
          ).length;

          return [
            {
              title: "Total Plants",
              value: () => totalPlants.toString(),
              valueColor: "primary.main",
              variant: "with-icon" as const,
            },
            {
              title: "Active",
              value: () => activePlants.toString(),
              valueColor: "success.main",
            },
            {
              title: "Maintenance",
              value: () => maintenancePlants.toString(),
              valueColor: "warning.main",
            },
            {
              title: "Offline",
              value: () => offlinePlants.toString(),
              valueColor: "error.main",
            },
          ];
        },
        permissions: {
          requiresAuth: true,
          requiresAdmin: false,
        },
      },
    },
  },
  {
    name: "batch-tickets",
    options: {
      enableStorage: true,
      enableErrorNotifications: true,
      idField: "id",
      serviceView: {
        enableServiceView: false,
        summaryCards: (rootStore) => {
          const store = rootStore.batchTicketsStore as {
            entities?: unknown[];
          };
          const entities = store?.entities || [];
          const totalTickets = entities.length;
          const todayTickets = entities.filter(
            (ticket: Record<string, unknown>) => {
              const ticketDate = new Date(ticket.createdAt as string);
              const today = new Date();
              today.setHours(0, 0, 0, 0);
              return ticketDate >= today;
            }
          ).length;
          const completedTickets = entities.filter(
            (ticket: Record<string, unknown>) =>
              ticket.status === "completed" || ticket.status === "delivered"
          ).length;
          const pendingTickets = entities.filter(
            (ticket: Record<string, unknown>) =>
              ticket.status === "pending" || ticket.status === "in_progress"
          ).length;

          return [
            {
              title: "Total Tickets",
              value: () => totalTickets.toString(),
              valueColor: "primary.main",
            },
            {
              title: "Today",
              value: () => todayTickets.toString(),
              valueColor: "info.main",
            },
            {
              title: "Completed",
              value: () => completedTickets.toString(),
              valueColor: "success.main",
            },
            {
              title: "Pending",
              value: () => pendingTickets.toString(),
              valueColor: "warning.main",
            },
          ];
        },
        permissions: {
          requiresAuth: true,
          requiresAdmin: false,
        },
        defaultSort: {
          sortBy: ["createdAt"],
          sortDirection: { createdAt: "DESC" },
        },
      },
    },
  },
  {
    name: "batches",
    options: {
      enableStorage: true,
      enableErrorNotifications: true,
      idField: "id",
      serviceView: {
        enableServiceView: false,
        summaryCards: (rootStore) => {
          const store = rootStore.batchesStore as {
            entities?: Record<string, unknown>[];
          };
          const entities = store?.entities || [];
          const totalBatches = entities.length;
          const todayBatches = entities.filter(
            (batch) => {
              const batchDate = new Date(batch.createdAt as string);
              const today = new Date();
              today.setHours(0, 0, 0, 0);
              return batchDate >= today;
            }
          ).length;
          const totalVolume = entities.reduce(
            (sum, batch) => {
              return sum + ((batch.volume as number) || 0);
            },
            0
          );
          const avgStrength = entities.reduce(
            (sum, batch) => {
              return sum + ((batch.strength as number) || 0);
            },
            0
          ) / Math.max(entities.length, 1);

          return [
            {
              title: "Total Batches",
              value: () => totalBatches.toString(),
              valueColor: "primary.main",
            },
            {
              title: "Today",
              value: () => todayBatches.toString(),
              valueColor: "info.main",
            },
            {
              title: "Total Volume (cy)",
              value: () => totalVolume.toFixed(0),
              valueColor: "success.main",
            },
            {
              title: "Avg Strength (psi)",
              value: () => avgStrength.toFixed(0),
              valueColor: "warning.main",
            },
          ];
        },
        permissions: {
          requiresAuth: true,
          requiresAdmin: false,
        },
        defaultSort: {
          sortBy: ["createdAt"],
          sortDirection: { createdAt: "DESC" },
        },
      },
    },
  },
  {
    name: "material-inventory",
    options: {
      enableStorage: true,
      enableErrorNotifications: true,
      idField: "id",
      serviceView: {
        enableServiceView: false,
        summaryCards: (rootStore) => {
          const store = rootStore.materialInventoryStore as {
            entities?: Record<string, unknown>[];
          };
          const entities = store?.entities || [];
          const totalItems = entities.length;
          const lowStock = entities.filter(
            (item) => {
              const current = item.currentQuantity as number;
              const min = item.minimumQuantity as number;
              return current < min;
            }
          ).length;
          const outOfStock = entities.filter(
            (item) =>
              (item.currentQuantity as number) === 0
          ).length;
          const totalValue = entities.reduce(
            (sum, item) => {
              const qty = item.currentQuantity as number;
              const price = item.unitPrice as number;
              return sum + (qty * price || 0);
            },
            0
          );

          return [
            {
              title: "Total Items",
              value: () => totalItems.toString(),
              valueColor: "primary.main",
            },
            {
              title: "Low Stock",
              value: () => lowStock.toString(),
              valueColor: "warning.main",
            },
            {
              title: "Out of Stock",
              value: () => outOfStock.toString(),
              valueColor: "error.main",
            },
            {
              title: "Total Value",
              value: () => `$${(totalValue / 1000).toFixed(1)}K`,
              valueColor: "info.main",
            },
          ];
        },
        permissions: {
          requiresAuth: true,
          requiresAdmin: false,
        },
      },
    },
  },
  {
    name: "material-locations",
    options: {
      enableStorage: true,
      enableErrorNotifications: true,
      idField: "id",
      serviceView: {
        enableServiceView: false,
        summaryCards: (rootStore) => {
          const store = rootStore.materialLocationsStore as {
            entities?: unknown[];
          };
          const entities = store?.entities || [];
          const totalLocations = entities.length;
          const activeLocations = entities.filter(
            (loc: Record<string, unknown>) =>
              loc.isActive !== false
          ).length;
          const warehouseLocations = entities.filter(
            (loc: Record<string, unknown>) =>
              loc.locationType === "warehouse"
          ).length;
          const yardLocations = entities.filter(
            (loc: Record<string, unknown>) =>
              loc.locationType === "yard"
          ).length;

          return [
            {
              title: "Total Locations",
              value: () => totalLocations.toString(),
              valueColor: "primary.main",
            },
            {
              title: "Active",
              value: () => activeLocations.toString(),
              valueColor: "success.main",
            },
            {
              title: "Warehouse",
              value: () => warehouseLocations.toString(),
              valueColor: "info.main",
            },
            {
              title: "Yard",
              value: () => yardLocations.toString(),
              valueColor: "warning.main",
            },
          ];
        },
        permissions: {
          requiresAuth: true,
          requiresAdmin: false,
        },
      },
    },
  },
  {
    name: "material-quality-certs",
    options: {
      enableStorage: true,
      enableErrorNotifications: true,
      idField: "id",
      serviceView: {
        enableServiceView: false,
        summaryCards: (rootStore) => {
          const store = rootStore.materialQualityCertsStore as {
            entities?: unknown[];
          };
          const entities = store?.entities || [];
          const totalCerts = entities.length;
          const validCerts = entities.filter(
            (cert: Record<string, unknown>) => {
              const expiry = new Date(cert.expiryDate as string);
              return expiry > new Date();
            }
          ).length;
          const expiringCerts = entities.filter(
            (cert: Record<string, unknown>) => {
              const expiry = new Date(cert.expiryDate as string);
              const thirtyDaysFromNow = new Date();
              thirtyDaysFromNow.setDate(thirtyDaysFromNow.getDate() + 30);
              return expiry <= thirtyDaysFromNow && expiry > new Date();
            }
          ).length;
          const expiredCerts = entities.filter(
            (cert: Record<string, unknown>) => {
              const expiry = new Date(cert.expiryDate as string);
              return expiry <= new Date();
            }
          ).length;

          return [
            {
              title: "Total Certs",
              value: () => totalCerts.toString(),
              valueColor: "primary.main",
            },
            {
              title: "Valid",
              value: () => validCerts.toString(),
              valueColor: "success.main",
            },
            {
              title: "Expiring (30d)",
              value: () => expiringCerts.toString(),
              valueColor: "warning.main",
            },
            {
              title: "Expired",
              value: () => expiredCerts.toString(),
              valueColor: "error.main",
            },
          ];
        },
        permissions: {
          requiresAuth: true,
          requiresAdmin: false,
        },
      },
    },
  },
  {
    name: "material-reorder-rules",
    options: {
      enableStorage: true,
      enableErrorNotifications: true,
      idField: "id",
      serviceView: {
        enableServiceView: false,
        summaryCards: (rootStore) => {
          const store = rootStore.materialReorderRulesStore as {
            entities?: unknown[];
          };
          const entities = store?.entities || [];
          const totalRules = entities.length;
          const activeRules = entities.filter(
            (rule: Record<string, unknown>) =>
              rule.isActive !== false
          ).length;
          const triggeredRules = entities.filter(
            (rule: Record<string, unknown>) =>
              rule.status === "triggered" || rule.lastTriggered
          ).length;

          return [
            {
              title: "Total Rules",
              value: () => totalRules.toString(),
              valueColor: "primary.main",
            },
            {
              title: "Active",
              value: () => activeRules.toString(),
              valueColor: "success.main",
            },
            {
              title: "Triggered",
              value: () => triggeredRules.toString(),
              valueColor: "warning.main",
            },
          ];
        },
        permissions: {
          requiresAuth: true,
          requiresAdmin: false,
        },
      },
    },
  },
  {
    name: "material-transactions",
    options: {
      enableStorage: true,
      enableErrorNotifications: true,
      idField: "id",
      serviceView: {
        enableServiceView: false,
        summaryCards: (rootStore) => {
          const store = rootStore.materialTransactionsStore as {
            entities?: unknown[];
          };
          const entities = store?.entities || [];
          const totalTransactions = entities.length;
          const todayTransactions = entities.filter(
            (txn: Record<string, unknown>) => {
              const txnDate = new Date(txn.createdAt as string);
              const today = new Date();
              today.setHours(0, 0, 0, 0);
              return txnDate >= today;
            }
          ).length;
          const receipts = entities.filter(
            (txn: Record<string, unknown>) =>
              txn.transactionType === "receipt" || txn.transactionType === "in"
          ).length;
          const issues = entities.filter(
            (txn: Record<string, unknown>) =>
              txn.transactionType === "issue" || txn.transactionType === "out"
          ).length;

          return [
            {
              title: "Total Transactions",
              value: () => totalTransactions.toString(),
              valueColor: "primary.main",
            },
            {
              title: "Today",
              value: () => todayTransactions.toString(),
              valueColor: "info.main",
            },
            {
              title: "Receipts",
              value: () => receipts.toString(),
              valueColor: "success.main",
            },
            {
              title: "Issues",
              value: () => issues.toString(),
              valueColor: "warning.main",
            },
          ];
        },
        permissions: {
          requiresAuth: true,
          requiresAdmin: false,
        },
        defaultSort: {
          sortBy: ["createdAt"],
          sortDirection: { createdAt: "DESC" },
        },
      },
    },
  },
  {
    name: "material-vendors",
    options: {
      enableStorage: true,
      enableErrorNotifications: true,
      idField: "id",
      serviceView: {
        enableServiceView: false,
        summaryCards: (rootStore) => {
          const store = rootStore.materialVendorsStore as {
            entities?: unknown[];
          };
          const entities = store?.entities || [];
          const totalVendors = entities.length;
          const activeVendors = entities.filter(
            (vendor: Record<string, unknown>) =>
              vendor.status === "active" || vendor.isActive !== false
          ).length;
          const preferredVendors = entities.filter(
            (vendor: Record<string, unknown>) =>
              vendor.isPreferred === true
          ).length;
          const certifiedVendors = entities.filter(
            (vendor: Record<string, unknown>) =>
              vendor.hasCertification === true || vendor.certificationStatus === "valid"
          ).length;

          return [
            {
              title: "Total Vendors",
              value: () => totalVendors.toString(),
              valueColor: "primary.main",
            },
            {
              title: "Active",
              value: () => activeVendors.toString(),
              valueColor: "success.main",
            },
            {
              title: "Preferred",
              value: () => preferredVendors.toString(),
              valueColor: "warning.main",
            },
            {
              title: "Certified",
              value: () => certifiedVendors.toString(),
              valueColor: "info.main",
            },
          ];
        },
        permissions: {
          requiresAuth: true,
          requiresAdmin: false,
        },
      },
    },
  },
  {
    name: "materials",
    options: {
      enableStorage: true,
      enableErrorNotifications: true,
      idField: "id",
      serviceView: {
        enableServiceView: false,
        summaryCards: (rootStore) => {
          const store = rootStore.materialsStore as {
            entities?: unknown[];
          };
          const entities = store?.entities || [];
          const totalMaterials = entities.length;
          const activeMaterials = entities.filter(
            (material: Record<string, unknown>) =>
              material.isActive !== false
          ).length;
          const rawMaterials = entities.filter(
            (material: Record<string, unknown>) =>
              material.materialType === "raw"
          ).length;
          const finishedMaterials = entities.filter(
            (material: Record<string, unknown>) =>
              material.materialType === "finished"
          ).length;

          return [
            {
              title: "Total Materials",
              value: () => totalMaterials.toString(),
              valueColor: "primary.main",
            },
            {
              title: "Active",
              value: () => activeMaterials.toString(),
              valueColor: "success.main",
            },
            {
              title: "Raw",
              value: () => rawMaterials.toString(),
              valueColor: "info.main",
            },
            {
              title: "Finished",
              value: () => finishedMaterials.toString(),
              valueColor: "warning.main",
            },
          ];
        },
        permissions: {
          requiresAuth: true,
          requiresAdmin: false,
        },
      },
    },
  },
  {
    name: "mix-designs",
    options: {
      enableStorage: true,
      enableErrorNotifications: true,
      idField: "id",
      serviceView: {
        enableServiceView: false,
        summaryCards: (rootStore) => {
          const store = rootStore.mixDesignsStore as {
            entities?: unknown[];
          };
          const entities = store?.entities || [];
          const totalDesigns = entities.length;
          const activeDesigns = entities.filter(
            (design: Record<string, unknown>) =>
              design.status === "active" || design.isActive !== false
          ).length;
          const approvedDesigns = entities.filter(
            (design: Record<string, unknown>) =>
              design.status === "approved"
          ).length;
          const draftDesigns = entities.filter(
            (design: Record<string, unknown>) =>
              design.status === "draft"
          ).length;

          return [
            {
              title: "Total Designs",
              value: () => totalDesigns.toString(),
              valueColor: "primary.main",
            },
            {
              title: "Active",
              value: () => activeDesigns.toString(),
              valueColor: "success.main",
            },
            {
              title: "Approved",
              value: () => approvedDesigns.toString(),
              valueColor: "info.main",
            },
            {
              title: "Drafts",
              value: () => draftDesigns.toString(),
              valueColor: "warning.main",
            },
          ];
        },
        permissions: {
          requiresAuth: true,
          requiresAdmin: false,
        },
      },
    },
  },
];